import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import AppTopBar from './AppTopBar';
import Sidebar from './Sidebar';
import PageBar from './PageBar';

/* ── LE CADRE DE L'APPLICATION (2026-09-17) ──────────────────────────────
   Bandeau des modules, menu de la section ouverte, ligne de commandes, puis
   le contenu. Il était écrit dans `App.js`, mêlé au routage de soixante-dix
   pages : on ne pouvait ni le lire ni le monter seul.

   Il ne connaît AUCUNE page. Ce qu'il porte lui vient :
   - de la configuration `NAV` (les modules et leurs sections), passée en prop
     et jamais recopiée ici ;
   - des registres `pageExports` et `pageUnit`, que la page alimente et que la
     ligne de commandes lit.
   Une page ne dessine donc jamais un morceau de cadre. */
export default function AppShell({
  // Navigation
  page, setPage, nav, lockedModules = [], scopes, isPlatformAdmin = false, canManageSettings = false, isTenantAdmin = false,
  canExportData = false,
  // Client et session
  tenants, activeTenant, onTenantChange, onLogout,
  userEmail, userName, userRoleLabel,
  // État du menu
  pinned, onTogglePin, backKey, backLabel, onBack,
  // Sous-états portés par le menu
  settingsTab, onGoSettingsTab, adminTab, onGoAdminTab,
  cockpitTab, onGoCockpitTab, cashTab, onGoCashTab,
  // Volet Savoirs
  knowledgeOpen, onToggleKnowledge, knowledgeDot = false,
  // Bandeaux d'alerte propres à l'application (droits refusés, etc.)
  banner = null,
  /* Volet de DROITE (Savoirs) : frère du contenu dans le cadre, il le pousse
     comme le menu le pousse à gauche. */
  rightPanel = null,
  children,
}) {
  const { t } = useTranslation();
  /* Le menu est une LIGNE : les modules vivent dans la barre, le panneau ne
     porte que les sections. Le cadre garde donc sa largeur en permanence, et le
     panneau se replie sans emporter la navigation avec lui. */

  return (
    <>
      <AppTopBar
        tenants={tenants} activeTenant={activeTenant} onTenantChange={onTenantChange}
        onHome={() => setPage('home')}
        canManageSettings={canManageSettings}
        onGoSettings={() => setPage('settings-hub')}
        onGoClosing={() => { onGoSettingsTab('finance'); setPage('settings-hub'); }}
        settingsActive={page === 'settings-hub'} page={page}
        isPlatformAdmin={isPlatformAdmin}
        navCollapsed={!pinned}
        onToggleKnowledge={onToggleKnowledge}
        knowledgeOpen={knowledgeOpen} knowledgeDot={knowledgeDot}
      />
      <div className="app-body app-body--pinned">
        <Sidebar
          page={page} setPage={setPage}
          onGoCashTab={onGoCashTab} cashTab={cashTab}
          pinned={pinned} onTogglePin={onTogglePin}
          activeTenant={activeTenant} onLogout={onLogout}
          nav={nav} lockedModules={lockedModules} isPlatformAdmin={isPlatformAdmin}
          userEmail={userEmail} userName={userName} userRoleLabel={userRoleLabel}
          canManageSettings={canManageSettings} isTenantAdmin={isTenantAdmin}
          canExportData={canExportData}
          settingsTab={settingsTab} onGoSettingsTab={onGoSettingsTab}
          adminTab={adminTab} onGoAdminTab={onGoAdminTab}
          scopes={scopes}
          cockpitTab={cockpitTab} onGoCockpitTab={onGoCockpitTab}
        />
        <div className="app-main">
          {/* La bande se téléporte dans la ligne du titre quand la page en
              offre une ; la clé lui dit de rechercher son nœud à chaque
              changement d'écran. */}
          {/* FERMER ET REVENIR, dans la fenêtre principale et bien visible
              (demande d'Olivier, 2026-09-29) : il vivait en tête du panneau du
              menu, où il passait inaperçu. Posé ICI, par le cadre, il vaut
              toujours pour TOUTES les pages du lieu (Paramètres, Administration,
              Atelier) et nomme l'écran quitté. */}
          {backKey && (
            <button type="button" className="app-back" onClick={onBack}>
              <ArrowLeft size={16} strokeWidth={2} aria-hidden="true" />
              <span>{backLabel ? t('settings:backTo', { page: backLabel }) : t('settings:backPrev')}</span>
            </button>
          )}
          <PageBar hostKey={page} />
          {banner}
          {children}
        </div>
        {rightPanel}
      </div>
    </>
  );
}
